import { onScopeDispose, readonly, ref, watch, type Ref } from 'vue'

const DURATION_MS = 640

function skipsAnimation(): boolean {
  if (typeof matchMedia !== 'function' || typeof requestAnimationFrame !== 'function') return true
  return matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Decelerating ease, so the number lands rather than stops. */
function easeOut(progress: number): number {
  return 1 - (1 - progress) ** 3
}

/** Counts from the value on screen up to `target` on mount and on change; an unchanged target animates nothing, and reduced motion lands instantly. */
export function useCountUp(target: Ref<number>): Readonly<Ref<number>> {
  const displayed = ref(target.value)
  let frame: number | null = null

  function stop(): void {
    if (frame === null) return
    cancelAnimationFrame(frame)
    frame = null
  }

  function run(to: number): void {
    stop()
    if (skipsAnimation()) {
      displayed.value = to
      return
    }

    const from = displayed.value
    const start = performance.now()

    function step(now: number): void {
      const progress = Math.min(1, (now - start) / DURATION_MS)
      displayed.value = from + (to - from) * easeOut(progress)
      frame = progress < 1 ? requestAnimationFrame(step) : null
    }

    frame = requestAnimationFrame(step)
  }

  watch(target, (to, from) => {
    if (to !== from) run(to)
  })

  run(target.value)
  onScopeDispose(stop)

  return readonly(displayed)
}
